import { createStore } from 'vuex'

export default createStore({
  state: {
    locale: "en-US",/* A string with a BCP 47 language tag */
    currency: "USD",/* locations currency code, */
    intro: {/* used for the intro / initial view */
      title: "Calculate the impact of AI-powered personalization on your program",
      paragraph: "Maximize the effectiveness and efficiency of your patient support programs and omni-channel pharmaceutical marketing with AllazoHealth. Using artificial intelligence and patient-level data, we can uniquely help you personalize interventions and create the optimal experience for every individual to boost therapy initiation, adherence, and outcomes. Find out how much impact our AI solution can deliver by answering a few questions about your program.",
      buttonText: "Start Now",
      imgSrc: "intro/AH_calculator_HERO_Image.png",
      mobileImgSrc: "intro/AH_calculator_HERO_Image_mobile.png",
      imgAlt: "intro image"
    },
    screener: {/* used for the screener question which sets the main goal */
      pageNumber: 1,
      note: "Objectives",
      title: "What is your primary goal?",
      cards: [
        {
          goal: "initiation",
          want: "I want to <span>increase</span>",
          action: "Therapy<br>Initiation"
        },{
          goal: "adherence",
          want: "I want to <span>boost</span>",
          action: "Medication<br>Adherence"
        },{
          goal: "both",
          want: "I want to <span>achieve</span>",
          action: "Both"
        }
      ],
      buttonText: "Next Question"
    },
    questions: [/* a question view will be generated for every question object in this array */
      {/* the identifier corresponds to a key/value pair in the store within the questionValues object below */
        allowZeroValue: false,
        identifier: "netRevenuePerRxPerMonth",
        valueType: "dollars",
        shareNote: "Revenue",
        question: "What is your estimated monthly net revenue per Rx?",
        subNote: "",
        previousButtonText: "Previous Question",
        nextButtonText: "Next Question"
      },
      {
        allowZeroValue: false,
        identifier: "averageDaysOnTherapyPerNewPtStart",
        valueType: "days",
        shareNote: "Adherence",
        question: "What is your patients’ average number of days on therapy?",
        subNote: "",
        previousButtonText: "Previous Question",
        nextButtonText: "Next Question"
      },
      {
        allowZeroValue: false,
        identifier: "numberOfPatientsEnrolledInYourAdherencePspAndOrPatientMarketingProgramsWhoHaveInitiatedTherapy",
        valueType: "number",
        shareNote: "Enrollment/Initiation",
        question: "How many patients are enrolled in your adherence PSP and/or patient marketing programs have initiated therapy?",
        subNote: "",
        previousButtonText: "Previous Question",
        nextButtonText: "Next Question"
      },
      {
        allowZeroValue: true,
        identifier: "newEnrolleesInYourPspOrPatientMarketingProgramsPerMonth",
        valueType: "number",
        shareNote: "Acquisition",
        question: "How many new enrollees do you typically generate per month?",
        subNote: "If unknown or not applicable enter 0",
        previousButtonText: "Previous Question",
        nextButtonText: "Next Question"
      }
    ],
    results: {/* used for the results page */
      retakeAssessmentText: "Retake Assessment",
      retakeAssessmentQuestionIndex: 1,
      retakeAssessmentPath: "/cost-calculator/",
      initiation: {/* these top level key names correspond to the "goal" values in the "screener" > "cards" objects which are the possible options to set the "mainGoal" value */
        headline: "Here’s Your Projected Impact with AllazoHealth",
        romanceParagraphs: ["Explore your potential impact on initiation with AllazoHealth below – and see how you can truly maximize your efforts by targeting both initiation and adherence. Addressing both of these critical areas together has been shown to synergistically and significantly improve performance."],
        slider: {
          togglePrompt: "TOGGLE RESULTS HERE",/* this value being filled in determines if the toggle shows or not */
          oneTitle: "Initiation",
          twoTitle: "Initiation + Adherence"
        },
        calculateAgainButtonText: "Calculate again",
        cta: {
          prompt: "Want to learn more?",
          promptDescription: ["Contact an AllazoHealth representative to discuss your results and get a free demo today."],
          linkText: "Request a Demo",
          linkUrl: "https://allazohealth.com/schedule-a-demo/"
        },
        related: {
          title: "Related Resources",
          subText: ["Check out these additional resources from AllazoHealth."],
          resources: [
            {
              imgSrc: "results/AllazoHealth_eBook_Why-Pharma-Marketers-Need-Artifical-Intelligence-now_cover.jpg",
              imgAlt: "AllazoHealth eBook Why Pharma Marketers Need Artifical Intelligence now",
              relatedContentUrl: "https://info.allazohealth.com/hubfs/Allazo_Health_The_New_Healthcare_Economy_eBook_Marketing_01_27_23_v3.pdf"
            },
            {
              imgSrc: "results/AllazoHealth_eBook_Why-PSPs-Need-Artifical-Intelligence-now_cover.jpg",
              imgAlt: "AllazoHealth eBook Why PSPs Need Artifical Intelligence now",
              relatedContentUrl: "https://info.allazohealth.com/hubfs/Allazo_Health_The_New_Healthcare_Economy_eBook_PSP_version_01_27_23_V3.pdf"
            },
            {
              imgSrc: "results/5-Best-Practices-from-Pharma-Companies-Embracing-Predictive-AI.jpg",
              imgAlt: "5 Best Practices from Pharma Companies Embracing Predictive AI",
              relatedContentUrl: "https://info.allazohealth.com/hubfs/Allazo_Health_eguide_5%20Case%20Studies%20for%20Embracing%20AI-Powered%20Personalization%20in%20the%20Pharmaceutical%20Industry%20_05_17_23.pdf"
            }
          ]
        },
        caseStudy: {
          heading: "Adherence Case Study Headline goes here",
          paragraphs: ["Lorem Ipsum is simply dummy text of the printing and typesetting indusstry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500's when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960's with the release of Letraset sheets containing Lorem Ipsum."],
          imgSrc: "results/adhere-img.jpg",
          imgAlt: "Adherence Case Study Cover FPO"
        }
      },
      adherence: {
        headline: "Here’s Your Projected Impact with AllazoHealth",
        romanceParagraphs: ["Explore your potential impact on adherence with AllazoHealth below – and see how you can truly maximize your efforts by targeting both initiation and adherence. Addressing both of these critical areas together has been shown to synergistically and significantly improve performance."],
        slider: {
          togglePrompt: "TOGGLE RESULTS HERE",
          oneTitle: "Adherence",
          twoTitle: "Adherence + Initiation"
        },
        calculateAgainButtonText: "Calculate again",
        cta: {
          prompt: "Want to learn more?",
          promptDescription: ["Contact an AllazoHealth representative to discuss your results and get a free demo today."],
          linkText: "Request a Demo",
          linkUrl: "https://allazohealth.com/schedule-a-demo/"
        },
        related: {
          title: "Related Resources",
          subText: ["Check out these additional resources from AllazoHealth."],
          resources: [
            {
              imgSrc: "results/AllazoHealth_eBook_Why-Pharma-Marketers-Need-Artifical-Intelligence-now_cover.jpg",
              imgAlt: "AllazoHealth eBook Why Pharma Marketers Need Artifical Intelligence now",
              relatedContentUrl: "https://info.allazohealth.com/hubfs/Allazo_Health_The_New_Healthcare_Economy_eBook_Marketing_01_27_23_v3.pdf"
            },
            {
              imgSrc: "results/AllazoHealth_eBook_Why-PSPs-Need-Artifical-Intelligence-now_cover.jpg",
              imgAlt: "AllazoHealth eBook Why PSPs Need Artifical Intelligence now",
              relatedContentUrl: "https://info.allazohealth.com/hubfs/Allazo_Health_The_New_Healthcare_Economy_eBook_PSP_version_01_27_23_V3.pdf"
            },
            {
              imgSrc: "results/5-Best-Practices-from-Pharma-Companies-Embracing-Predictive-AI.jpg",
              imgAlt: "5 Best Practices from Pharma Companies Embracing Predictive AI",
              relatedContentUrl: "https://info.allazohealth.com/hubfs/Allazo_Health_eguide_5%20Case%20Studies%20for%20Embracing%20AI-Powered%20Personalization%20in%20the%20Pharmaceutical%20Industry%20_05_17_23.pdf"
            }
          ]
        },
        caseStudy: {
          heading: "Adherence Case Study Headline goes here",
          paragraphs: ["Lorem Ipsum is simply dummy text of the printing and typesetting indusstry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500's when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960's with the release of Letraset sheets containing Lorem Ipsum."],
          imgSrc: "results/adhere-img.jpg",
          imgAlt: "Adherence Case Study Cover FPO"
        }
      },
      both: {
        headline: "Here’s Your Projected Impact with AllazoHealth",
        romanceParagraphs: ["Explore your potential impact with AllazoHealth below – and see how you can truly maximize your impact by targeting both initiation and adherence. Addressing both of these critical areas together has been shown to synergistically and significantly improve performance."],
        slider: {
          togglePrompt: "",
          oneTitle: "Adherence + Initiation",
          twoTitle: ""
        },
        calculateAgainButtonText: "Calculate again",
        cta: {
          prompt: "Want to learn more?",
          promptDescription: ["Contact an AllazoHealth representative to discuss your results and get a free demo today."],
          linkText: "Request a Demo",
          linkUrl: "https://allazohealth.com/schedule-a-demo/"
        },
        related: {
          title: "Related Resources",
          subText: ["Check out these additional resources from AllazoHealth."],
          resources: [
            {
              imgSrc: "results/AllazoHealth_eBook_Why-Pharma-Marketers-Need-Artifical-Intelligence-now_cover.jpg",
              imgAlt: "AllazoHealth eBook Why Pharma Marketers Need Artifical Intelligence now",
              relatedContentUrl: "/pharma-marketing-ebook-pharmaceutical-marketing-needs-ai-now/"
            },
            {
              imgSrc: "results/AllazoHealth_eBook_Why-PSPs-Need-Artifical-Intelligence-now_cover.jpg",
              imgAlt: "AllazoHealth eBook Why PSPs Need Artifical Intelligence now",
              relatedContentUrl: "/why-patient-support-programs-need-ai-now/"
            },
            {
              imgSrc: "results/5-Best-Practices-from-Pharma-Companies-Embracing-Predictive-AI.jpg",
              imgAlt: "5 Best Practices from Pharma Companies Embracing Predictive AI",
              relatedContentUrl: "/5-case-studies-for-embracing-ai-powered-personalization-in-the-pharmaceutical-industry/"
            }
          ]
        },
        caseStudy: {
          heading: "Adherence Case Study Headline goes here",
          paragraphs: ["Lorem Ipsum is simply dummy text of the printing and typesetting indusstry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500's when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960's with the release of Letraset sheets containing Lorem Ipsum."],
          imgSrc: "results/adhere-img.jpg",
          imgAlt: "Adherence Case Study Cover FPO"
        }
      },
      graph: {
        keys: {
          standard: [
            {
              value: "<span class=\"box-text\">Conservative</span><br> Projected Impact",
              showForBoth: false
            },
            {
              value: "<span class=\"box-text\">Average</span><br> Projected Impact",
              showForBoth: false
            },
            {
              value: "<span class=\"box-text\">Optimistic</span><br> Projected Impact",
              showForBoth: true
            }
          ],
          initiation: "<span class=\"box-text\">+ Adherence</span>",
          adherence: "<span class=\"box-text\">+ Initiation</span>"
        },
        yMax: 18,
        bottomLabels: ["Conservative<br>Projected<br>Impact", "Average<br>Projected<br>Impact", "Optimistic<br>Projected<br>Impact"]
      }
    },
    numberOfPages: 0,
    currentPage: 0,
    mainGoal: "",/* this value will be one of the "goal values in the "screener" > "cards" > objects above and corresponds to a top-level key name in the "results" object above */
    questionValues: {/* these key names correspond to the "identifier" values in each of the "questions" objects above */
      netRevenuePerRxPerMonth: 0,
      averageDaysOnTherapyPerNewPtStart: 0,
      newEnrolleesInYourPspOrPatientMarketingProgramsPerMonth: 0,
      numberOfPatientsEnrolledInYourAdherencePspAndOrPatientMarketingProgramsWhoHaveInitiatedTherapy: 0
    },
    showFullGraph: false
  },
  getters: {
    /* BEGIN - Higher Therapy Initiation (if only Therapy Initiation goal is selected) */
    rxPerNewPtEnrolled30DaysEquivalent (state) {
      return state.questionValues.averageDaysOnTherapyPerNewPtStart / 30
    },
    netRevenuePerNewPtInitiated (state, getters) {
      return getters.rxPerNewPtEnrolled30DaysEquivalent * state.questionValues.netRevenuePerRxPerMonth
    },
    newEnrolleesPerYear (state) {
      // if the user input 0, bump it up to 1
      const n = state.questionValues.newEnrolleesInYourPspOrPatientMarketingProgramsPerMonth
      const adjustedNumber = n === 0 ? 1 : n

      return adjustedNumber * 12
    },
    additionalPtsInitiatedDueToAI: (state, getters) => (percent) => {
      return getters.newEnrolleesPerYear * percent
    },
    impactPerYearOfInitiationPoweredByAI: (state, getters) => (percent) => {
      return getters.netRevenuePerNewPtInitiated * getters.additionalPtsInitiatedDueToAI(percent)
    },
    getConservativeInitiation (state, getters) {
      return getters.impactPerYearOfInitiationPoweredByAI(0.04)
    },
    getAverageInitiation (state, getters) {
      return getters.impactPerYearOfInitiationPoweredByAI(0.06)
    },
    getOptimisticInitiation (state, getters) {
      return getters.impactPerYearOfInitiationPoweredByAI(0.08)
    },
    /* END - Higher Therapy Initiation (if only Therapy Initiation goal is selected) */
    
    /* BEGIN - Higher Adherence (if only Adherence goal is selected) */
    currentRxMonthsPerPtInPSP (state) {
      return state.questionValues.averageDaysOnTherapyPerNewPtStart
    },
    incrementalRxDueToAI: (state, getters) => (percent) => {
      return state.questionValues.numberOfPatientsEnrolledInYourAdherencePspAndOrPatientMarketingProgramsWhoHaveInitiatedTherapy * (getters.currentRxMonthsPerPtInPSP / 30) * percent
    },
    incrementalNetRevenuePerYear: (state, getters) => (percent) => {
      return getters.incrementalRxDueToAI(percent) * state.questionValues.netRevenuePerRxPerMonth
    },
    getConservativeAdherence (state, getters) {
      return getters.incrementalNetRevenuePerYear(0.07)
    },
    getAverageAdherence (state, getters) {
      return getters.incrementalNetRevenuePerYear(0.09)
    },
    getOptimisticAdherence (state, getters) {
      return getters.incrementalNetRevenuePerYear(0.11)
    },
    /* END - Higher Adherence (if only Adherence goal is selected) */

    /* BEGIN - If Both Goals are selected */
    getTherapyInitiationImpact: (state, getters) => (initiationPercent, adherencePercent) => {
      // if the user input 0, bump it up to 1
      const n = state.questionValues.newEnrolleesInYourPspOrPatientMarketingProgramsPerMonth
      const adjustedNumber = n === 0 ? 1 : n
      // $B$33 × ( 1 + $A39 ) × $B$31 × ( D$36 ) × $B$29 × 12
      return getters.rxPerNewPtEnrolled30DaysEquivalent * (1 + adherencePercent) * adjustedNumber * (initiationPercent) * state.questionValues.netRevenuePerRxPerMonth * 12
    },
    getAdherrenceImpact: (state, getters) => (initiationPercent, adherencePercent) => {
      // $B$32 × ( 1 + D$46 ) × $B$33 × ( $A49 ) × $B$29
      return state.questionValues.numberOfPatientsEnrolledInYourAdherencePspAndOrPatientMarketingProgramsWhoHaveInitiatedTherapy * (1 + initiationPercent) * getters.rxPerNewPtEnrolled30DaysEquivalent * (adherencePercent) * state.questionValues.netRevenuePerRxPerMonth
    },
    getConservativeCombinedImpact(state, getters) {
      return getters.getTherapyInitiationImpact(0.04, 0.07) + getters.getAdherrenceImpact(0.04, 0.07)
    },
    getAverageCombinedImpact(state, getters) {
      return getters.getTherapyInitiationImpact(0.06, 0.09) + getters.getAdherrenceImpact(0.06, 0.09)
    },
    getOptimisticCombinedImpact(state, getters) {
      return getters.getTherapyInitiationImpact(0.08, 0.11) + getters.getAdherrenceImpact(0.08, 0.11)
    }
    /* END - If Both Goals are selected */
  },
  mutations: {
    setNumberOfPages (state) {
      if(state.questions) {
        // add one for the intro and screener pages
        state.numberOfPages = state.questions.length + 2
      }
    },
    setCurrentPage (state, payload) {
      // adds 1 for screener
        state.currentPage = payload.incomingIndex;
    },
    decreaseCurrentPage (state) {
      if(state.currentPage > 0) {
        state.currentPage--;
      }
    },
    incrementCurrentPage (state) {
      // adds 1 for screener
      if(state.currentPage < (state.questions.length + 1)) {
        state.currentPage++;
      }
    },
    setStateValue(state, payload) {
      // used to update any value in state
      const key = payload.stateKey
      const incoming = payload.incomingValue

      if(state[key] !== incoming) {
        state[key] = incoming
      }
    },
    setQuestionValue(state, payload) {
      // used to update any value in state
      const key = payload.stateKey
      const incoming = payload.incomingValue

      if(state.questionValues[key] !== incoming) {
        state.questionValues[key] = incoming
      }
    },
  },
  actions: {
  },
  modules: {
  }
})
